import type { JsonSchemaType, Object as PortalObject, User, UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  AlertCircle,
  Box,
  CalendarRange,
  Database,
  Edit3,
  Eye,
  FileText,
  Info,
  Link2,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { DeepPartial } from 'ts-essentials';
import { z } from 'zod';
import {
  useCreateObject,
  useCustomer,
  useEntityPermissionOverview,
  useObject,
  useObjectCategories,
  useObjectCategory,
  useObjects,
  useSystemShip,
  useSystemShipDetail,
  useSystemShipSuggestions,
  useUpdateObject,
  useUserGroups,
} from '@/core/api';
import { PayloadApiError } from '@/core/api/client';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  EntityPermissionsTab,
  type PermissionCustomerContext,
  ViewAsDialog,
} from '@/features/shared/permissions';
import { MultiSearchCombobox, SearchCombobox } from '@/shared/components/inputs';
import {
  DetailPageLayout,
  EntityReferenceList,
  EntityReferenceValue,
  TabNav,
} from '@/shared/components/layout';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import { MetadataBuilderWithPreview } from '@/shared/components/metadata/metadata-builder/metadata-builder-with-preview';
import { MetadataReadView } from '@/shared/components/metadata/metadata-read-view';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { useViewAsState } from '@/shared/hooks/use-view-as';
import type { StatusOption, StatusValue } from '@/shared/schema';
import {
  getCustomerDetailHref,
  getObjectCategoryDetailHref,
  getObjectDetailHref,
  getSystemShipDetailHref,
  pickZodObjectFields,
  scrollToTarget,
} from '@/shared/utils';
import {
  createEmptyMetadataSchema,
  createMetadataZodSchema,
  getFieldPermission,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';
import { isHelicopterCategoryName, isOffshoreCategoryName } from '@/shared/utils/object-categories';
import { getObjectCategoryIcon } from '@/shared/utils/object-category-icons';
import {
  canReadService,
  HELI_PROVIDER_GROUP_KEY,
  JUH_DISPATCHER_GROUP_KEY,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { ObjectActivity } from '../components/object-activity';
import { ObjectFiles } from '../components/object-files';
import { ObjectMetadataSidemenu } from '../components/object-metadata-sidemenu';
import { useScrollSpy } from '../components/use-scroll-spy';
import { createObjectSchema } from '../schema';
import type { ObjectFormValues, Object as PortalViewObject } from '../types';

const ensureStatusOptionsWithIds = (statusOptions?: StatusOption[]) => {
  if (!statusOptions) return undefined;

  return statusOptions.map((option, index) => ({
    ...option,
    id: option.id?.trim() || `status-opt-${option.color}-${index + 1}`,
  }));
};

const SHIP_CATEGORY_NAMES = ['schiff', 'jack-up-schiff', 'jack up schiff', 'jack-up'];
// Categories JUH dispatchers may create/edit (see backend objects.utils.ts).
const DISPONENT_CREATE_CATEGORY_NAMES = ['insel', 'klinik', 'land', 'tankmöglichkeiten'];

const toPickerDate = (value?: string | null) => (value ? new Date(`${value}T12:00:00`) : undefined);

const toIsoDay = (value?: Date | null) =>
  value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : '';

const formatDay = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd.MM.yyyy') : '-';

const normalizeRelationshipId = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'id' in value) {
    const id = (value as { id?: unknown }).id;
    return typeof id === 'string' ? id : null;
  }
  return null;
};

type ShipAssignmentMode = 'reuse-existing' | 'create-new';

type ShipMasterDraft = {
  imo: string;
  flag: string;
  callSign: string;
  mmsi: string;
};

const EMPTY_SHIP_MASTER_DRAFT: ShipMasterDraft = {
  imo: '',
  flag: '',
  callSign: '',
  mmsi: '',
};

type ShipMasterDraftErrors = Partial<Record<keyof ShipMasterDraft, string>>;

export function ManageObject() {
  const { t, locale } = useIntl();
  const { user: currentUser } = useAuth();
  const createObject = useCreateObject();
  const updateObject = useUpdateObject();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const categoryFromQuery = searchParams.get('category');
  const isEditMode = !!id;
  const isCopyMode = !!copyFromId;
  const [viewMode, setViewMode] = useState<'read' | 'edit'>(() => (isEditMode ? 'read' : 'edit'));
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shipSubmitError, setShipSubmitError] = useState<string | null>(null);
  const [shipAssignmentMode, setShipAssignmentMode] =
    useState<ShipAssignmentMode>('reuse-existing');
  const [shipMasterDraft, setShipMasterDraft] = useState<ShipMasterDraft>(EMPTY_SHIP_MASTER_DRAFT);
  const [shipMasterDraftErrors, setShipMasterDraftErrors] = useState<ShipMasterDraftErrors>({});

  // Use regular or customer view object hooks based on mode
  const { data: object, isLoading: isLoadingObject } = useObject(id || '');
  const { data: objectToCopy } = useObject(copyFromId || '', {
    disabled: !copyFromId,
  });
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group as UserGroup])),
    [userGroups]
  );
  const currentObject = object;

  const resolveGroupFromUser = useCallback(
    (user: User | null | undefined): UserGroup | null => {
      if (!user?.userGroup) return null;
      if (typeof user.userGroup === 'string') {
        return userGroupsById.get(user.userGroup) ?? null;
      }
      return user.userGroup as UserGroup;
    },
    [userGroupsById]
  );

  const currentGroup = resolveGroupFromUser(currentUser);
  const isCurrentGmnDomain = resolveUserGroupDomain(currentUser) === 'gmn';
  const isCurrentGmnAdmin = currentGroup?.key === 'gmn-admin';
  const canUseViewAs = isCurrentGmnAdmin && isEditMode;

  const { viewAsUser, viewAsAccess, isViewAsActive, setViewAsUserId, clearViewAs } = useViewAsState(
    {
      entityType: 'objects',
      entityId: isEditMode ? id : null,
      enabled: canUseViewAs,
    }
  );

  const effectiveUser = viewAsUser ?? currentUser;
  const effectiveGroup = resolveGroupFromUser(effectiveUser);
  const effectiveGroupKey = effectiveGroup?.key ?? null;
  const effectiveDomain = effectiveGroup?.domain ?? null;
  const isEffectiveGmnAdmin = effectiveGroupKey === 'gmn-admin';
  const isEffectiveCustomerAdmin = effectiveGroupKey === 'customer-admin';
  const isEffectiveCustomerUser = effectiveGroupKey === 'customer-user';
  const isEffectiveJuhDispatcher = effectiveGroupKey === JUH_DISPATCHER_GROUP_KEY;
  const isGmnUser = effectiveDomain === 'gmn';
  // GMN staff with full object rights (excludes JUH dispatchers, who only manage
  // island/clinic/land/fuel-station objects). Used to gate GMN-admin-only fields.
  const isGmnAdminLevel = isGmnUser && !isEffectiveJuhDispatcher;
  const isCustomer = effectiveDomain === 'customer';
  const canReadObjects = effectiveGroup ? canReadService(effectiveGroup, 'objects') : false;
  const canReadCustomers = effectiveGroup ? canReadService(effectiveGroup, 'customers') : false;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  const assignedCustomerId =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.id
      : (currentObject?.assignedCustomer as string | undefined);
  const assignedHelicopterServiceProviderId =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.id
      : (currentObject?.assignedHelicopterServiceProvider as string | undefined);
  const currentObjectAssignmentOwner = useMemo(
    () =>
      assignedCustomerId
        ? {
            field: 'assignedCustomer' as const,
            id: assignedCustomerId,
          }
        : assignedHelicopterServiceProviderId
          ? {
              field: 'assignedHelicopterServiceProvider' as const,
              id: assignedHelicopterServiceProviderId,
            }
          : null,
    [assignedCustomerId, assignedHelicopterServiceProviderId]
  );

  // Determine if customer can update this object
  let objectCustomerIdForAccess: string | undefined;
  if (currentObject) {
    objectCustomerIdForAccess =
      effectiveGroupKey === HELI_PROVIDER_GROUP_KEY
        ? assignedHelicopterServiceProviderId
        : assignedCustomerId;
  }

  const permissionCustomerContexts = useMemo<PermissionCustomerContext[]>(() => {
    const contexts: PermissionCustomerContext[] = [];

    if (assignedCustomerId) {
      contexts.push({
        customerId: assignedCustomerId,
        source: 'assigned-customer',
      });
    }

    if (assignedHelicopterServiceProviderId) {
      contexts.push({
        customerId: assignedHelicopterServiceProviderId,
        source: 'assigned-helicopter-service-provider',
      });
    }

    const contextByCustomerId = new Map<string, PermissionCustomerContext>();

    for (const context of contexts) {
      const existing = contextByCustomerId.get(context.customerId);
      if (!existing) {
        contextByCustomerId.set(context.customerId, context);
        continue;
      }

      if (existing.source !== context.source) {
        contextByCustomerId.set(context.customerId, {
          customerId: context.customerId,
          source: 'combined',
        });
      }
    }

    return Array.from(contextByCustomerId.values());
  }, [assignedCustomerId, assignedHelicopterServiceProviderId]);

  const prioritizedPermissionCustomerContexts = useMemo(() => {
    if (!objectCustomerIdForAccess) return permissionCustomerContexts;

    const prioritized = permissionCustomerContexts.filter(
      (context) => context.customerId === objectCustomerIdForAccess
    );
    const remaining = permissionCustomerContexts.filter(
      (context) => context.customerId !== objectCustomerIdForAccess
    );

    return [...prioritized, ...remaining];
  }, [objectCustomerIdForAccess, permissionCustomerContexts]);

  const effectiveUserCustomerId =
    typeof effectiveUser?.customer === 'object'
      ? effectiveUser?.customer?.id
      : (effectiveUser?.customer as string | undefined);

  // A customer only "manages" objects that belong to them (their assignedCustomer,
  // or assignedHelicopterServiceProvider for heli-providers). Helicopters that are
  // only readable for linking are foreign/read-only for the customer — they should
  // not expose the permissions/activity tabs or trigger the personal access lookup.
  const isObjectManagedByEffectiveUser =
    isCustomer &&
    Boolean(effectiveUserCustomerId) &&
    Boolean(objectCustomerIdForAccess) &&
    objectCustomerIdForAccess === effectiveUserCustomerId;
  const isForeignReadonlyObjectForCustomer =
    isCustomer && Boolean(currentObject) && !isObjectManagedByEffectiveUser;
  const shipWindparkSelectionOwner = useMemo(
    () =>
      currentObjectAssignmentOwner ??
      (isCustomer && effectiveUserCustomerId
        ? {
            field:
              effectiveGroupKey === HELI_PROVIDER_GROUP_KEY
                ? ('assignedHelicopterServiceProvider' as const)
                : ('assignedCustomer' as const),
            id: effectiveUserCustomerId,
          }
        : null),
    [currentObjectAssignmentOwner, effectiveGroupKey, effectiveUserCustomerId, isCustomer]
  );

  const {
    data: currentAccessOverview,
    isLoading: isLoadingCurrentAccessOverview,
    isFetching: isFetchingCurrentAccessOverview,
  } = useEntityPermissionOverview({
    entityType: 'objects',
    entityId: id ?? '',
    userIds: currentUser?.id ? [currentUser.id] : [],
    disabled:
      !isEditMode ||
      !id ||
      effectiveDomain !== 'customer' ||
      isViewAsActive ||
      isForeignReadonlyObjectForCustomer,
  });
  const currentAccessEntry = currentAccessOverview?.entries?.[0] ?? null;
  const canCreateShipObjectAsCustomer =
    !isEditMode &&
    isCustomer &&
    (effectiveGroupKey === 'customer-admin' ||
      effectiveGroupKey === 'customer-user' ||
      effectiveGroupKey === 'heli-provider') &&
    Boolean(categoryFromQuery);

  const assignedCustomerIdForRead = assignedCustomerId;
  const { data: assignedCustomerForRead } = useCustomer(assignedCustomerIdForRead ?? '');

  const assignedHeliProviderIdForRead = assignedHelicopterServiceProviderId;
  const { data: assignedHeliProviderForRead } = useCustomer(assignedHeliProviderIdForRead ?? '');

  // Determine the appropriate back href based on context
  const backHrefBase = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';
  const backHref = `${backHrefBase}${categoryFromQuery ? `?category=${categoryFromQuery}` : ''}`;

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [categoryMetadataZodSchema, setCategoryMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [dynamicMetadataZodSchema, setDynamicMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const initialValuesRef = useRef<ObjectFormValues | null>(null);
  const previousHeliProviderIdRef = useRef<string | null>(null);
  const previousOffshoreProviderIdRef = useRef<string | null>(null);
  const autoPrefilledShipNameRef = useRef<string | null>(null);

  const updateShipMasterDraft = useCallback(
    <K extends keyof ShipMasterDraft>(key: K, value: ShipMasterDraft[K]) => {
      setShipMasterDraft((current) => ({
        ...current,
        [key]: value,
      }));
      setShipMasterDraftErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    },
    []
  );

  const initialCategory = !isEditMode && !isCopyMode && categoryFromQuery ? categoryFromQuery : '';
  const form = useForm<ObjectFormValues, any, ObjectFormValues>({
    resolver: zodResolver(
      createObjectSchema(t, categoryMetadataZodSchema, dynamicMetadataZodSchema)
    ),
    defaultValues: {
      name: '',
      description: '',
      category: initialCategory,
      systemShip: null,
      activePeriods: [],
      assignedCustomer: '',
      assignedHelicopterServiceProvider: '',
      linkedWindpark: null,
      linkedHelicopters: [],
      dynamicMetadataSchema: createEmptyMetadataSchema(),
      categoryMetadataValues: {},
      dynamicMetadataValues: {},
    },
  });
  const watchedActivePeriods = useWatch({
    control: form.control,
    name: 'activePeriods',
  });

  useEffect(() => {
    setViewMode(isEditMode ? 'read' : 'edit');
  }, [isEditMode, id]);

  // Track selected category ID for fetching its schema
  const selectedCategoryId = form.watch('category') || '';
  const { data: selectedCategory } = useObjectCategory(selectedCategoryId, {
    disabled: !selectedCategoryId,
  });
  const selectedCategoryName = selectedCategory?.name?.toLocaleLowerCase('de-DE') ?? null;
  const isHelicopterCategory = isHelicopterCategoryName(selectedCategoryName);
  const isOffshoreCategory = isOffshoreCategoryName(selectedCategoryName);
  const isShipCategory = SHIP_CATEGORY_NAMES.includes(selectedCategoryName || '');
  const isWindparkCategory = selectedCategoryName === 'windpark';
  const isShipCreateMode = isShipCategory && !isEditMode;
  const isDisponentCreateCategory = DISPONENT_CREATE_CATEGORY_NAMES.includes(
    selectedCategoryName || ''
  );
  // JUH dispatchers may create and edit island/clinic/land/fuel-station objects despite
  // having no generic object create/update permission (enforced in the backend).
  const canManageDisponentObject = isEffectiveJuhDispatcher && isDisponentCreateCategory;
  const assignmentDetailOptIn = searchParams.get('assignment') === '1';
  const canUpdateOwnShipObjectAsCustomerUser =
    isEditMode &&
    isCustomer &&
    isShipCategory &&
    effectiveUserCustomerId &&
    (effectiveGroupKey === 'customer-user' || effectiveGroupKey === 'heli-provider') &&
    currentObjectAssignmentOwner?.id === effectiveUserCustomerId;

  const canUpdateObject =
    !isLoadingObject &&
    (canCreateShipObjectAsCustomer ||
      canManageDisponentObject ||
      isEffectiveGmnAdmin ||
      canUpdateOwnShipObjectAsCustomerUser ||
      (isCustomer &&
        !isViewAsActive &&
        !isLoadingCurrentAccessOverview &&
        !isFetchingCurrentAccessOverview &&
        Boolean(currentAccessEntry?.update.allowed)));

  const viewAsUpdateAllowed = Boolean(viewAsAccess?.update.allowed);
  const canPreviewEditObject = isViewAsActive ? viewAsUpdateAllowed : canUpdateObject;
  const canSaveObject = !isViewAsActive && canPreviewEditObject;
  const formDisabled = isLoadingObject || !canPreviewEditObject;

  useEffect(() => {
    if (!isEditMode || !isShipCategory || !currentObject || assignmentDetailOptIn) {
      return;
    }

    const systemShipId = normalizeRelationshipId(currentObject.systemShip);
    if (!systemShipId) return;

    const href = getSystemShipDetailHref({
      systemShipId,
      domain: isCurrentGmnDomain ? 'gmn' : 'customer',
      canReadObjects: currentGroup ? canReadService(currentGroup, 'objects') : false,
    });
    if (!href) return;

    navigate(href, { replace: true });
  }, [
    assignmentDetailOptIn,
    currentGroup,
    currentObject,
    isCurrentGmnDomain,
    isEditMode,
    isShipCategory,
    navigate,
  ]);

  const selectedHeliProviderIdRaw = form.watch('assignedHelicopterServiceProvider');
  const selectedHeliProviderId =
    typeof selectedHeliProviderIdRaw === 'string' ? selectedHeliProviderIdRaw : '';
  const shipNameValue = form.watch('name') || '';
  const selectedSystemShipId = form.watch('systemShip') || '';
  const selectedAssignedCustomerIdRaw = form.watch('assignedCustomer');
  const selectedAssignedCustomerId =
    typeof selectedAssignedCustomerIdRaw === 'string' ? selectedAssignedCustomerIdRaw : '';
  const targetShipAssignmentCustomerId = isGmnUser
    ? selectedAssignedCustomerId || null
    : (effectiveUserCustomerId ?? null);
  const {
    fields: activePeriodFields,
    append: appendActivePeriod,
    remove: removeActivePeriod,
  } = useFieldArray({
    control: form.control,
    name: 'activePeriods',
  });
  const { data: selectedSystemShip } = useSystemShip(selectedSystemShipId, {
    disabled: !selectedSystemShipId,
  });
  const { data: selectedSystemShipDetail } = useSystemShipDetail(selectedSystemShipId, {
    disabled: !isShipCategory || !selectedSystemShipId,
  });
  const existingVisibleShipAssignmentId = useMemo(() => {
    if (!selectedSystemShipDetail) return null;
    if (!targetShipAssignmentCustomerId) return null;

    const candidates = [
      ...(selectedSystemShipDetail.activeObject ? [selectedSystemShipDetail.activeObject] : []),
      ...selectedSystemShipDetail.historicalObjects,
    ].filter(
      (candidate) =>
        normalizeRelationshipId(candidate.assignedCustomer) === targetShipAssignmentCustomerId
    );

    const seen = new Set<string>();
    const uniqueCandidates = candidates.filter((candidate) => {
      if (!candidate?.id || seen.has(candidate.id)) return false;
      seen.add(candidate.id);
      return true;
    });

    if (uniqueCandidates.length === 0) return null;

    uniqueCandidates.sort((left, right) =>
      (right.updatedAt ?? '').localeCompare(left.updatedAt ?? '')
    );

    return uniqueCandidates[0]?.id ?? null;
  }, [selectedSystemShipDetail, targetShipAssignmentCustomerId]);
  const { data: existingVisibleShipAssignment } = useObject(existingVisibleShipAssignmentId || '', {
    disabled: !existingVisibleShipAssignmentId,
  });
  const { data: systemShipSuggestions } = useSystemShipSuggestions(shipNameValue, {
    disabled: !isShipCategory || !!selectedSystemShipId || viewMode !== 'edit',
  });
  const { data: systemShipSuggestionsByImo } = useSystemShipSuggestions(shipMasterDraft.imo, {
    disabled:
      !isShipCreateMode ||
      shipAssignmentMode !== 'create-new' ||
      !!selectedSystemShipId ||
      viewMode !== 'edit',
  });

  const { data: helicopterCategoryOptions } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Helikopter' } }, { name: { equals: 'Rettungshubschrauber' } }],
    },
    limit: 20,
    disabled: !isHelicopterCategory && !isOffshoreCategory,
  });
  const helicopterCategoryIds = useMemo(
    () => (helicopterCategoryOptions?.docs || []).map((category) => String(category.id)),
    [helicopterCategoryOptions?.docs]
  );

  const { data: offshoreCategoryOptions } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Windpark' } }, { name: { equals: 'Plattform' } }],
    },
    limit: 20,
    disabled: !isHelicopterCategory,
  });
  const offshoreCategoryIds = useMemo(
    () => (offshoreCategoryOptions?.docs || []).map((category) => String(category.id)),
    [offshoreCategoryOptions?.docs]
  );

  const { data: windparkCategoryOptions } = useObjectCategories({
    where: {
      name: { equals: 'Windpark' },
    },
    limit: 200,
    disabled: !isShipCategory,
  });
  const windparkCategoryIds = useMemo(
    () => (windparkCategoryOptions?.docs || []).map((category) => String(category.id)),
    [windparkCategoryOptions?.docs]
  );

  const { data: shipCategoryOptions } = useObjectCategories({
    where: {
      or: [
        { name: { equals: 'Schiff' } },
        { name: { equals: 'Jack-Up-Schiff' } },
        { name: { equals: 'Jack Up Schiff' } },
        { name: { equals: 'Jack-Up' } },
      ],
    },
    limit: 200,
    disabled: !isShipCategory && !isWindparkCategory,
  });
  const shipCategoryIds = useMemo(
    () => (shipCategoryOptions?.docs || []).map((category) => String(category.id)),
    [shipCategoryOptions?.docs]
  );
  const occupiedShipPeriods = useMemo(() => {
    if (!selectedSystemShipDetail?.occupiedPeriods?.length) return [];

    const currentShipObjectId = id ?? existingVisibleShipAssignmentId;

    return selectedSystemShipDetail.occupiedPeriods
      .filter((period) => !period.objectId || period.objectId !== currentShipObjectId)
      .map((period) => ({
        startDate: period.startDate,
        endDate: period.endDate,
        start: new Date(`${period.startDate}T00:00:00`),
        end: new Date(`${period.endDate}T23:59:59`),
      }));
  }, [existingVisibleShipAssignmentId, id, selectedSystemShipDetail?.occupiedPeriods]);
  const selectedSystemShipSummary = useMemo(
    () => [
      { label: t('common.name'), value: selectedSystemShip?.name || '-' },
      { label: 'IMO', value: selectedSystemShip?.imo || '-' },
      {
        label: t('objects.ships.flagLabel'),
        value: selectedSystemShip?.flag || '-',
      },
      {
        label: t('objects.ships.callSignLabel'),
        value: selectedSystemShip?.callSign || '-',
      },
      { label: 'MMSI', value: selectedSystemShip?.mmsi || '-' },
    ],
    [selectedSystemShip, t]
  );
  const isShipNameMirrored = isShipCategory && Boolean(selectedSystemShipId);
  const hasExistingVisibleShipAssignment =
    isShipCreateMode &&
    shipAssignmentMode === 'reuse-existing' &&
    Boolean(existingVisibleShipAssignmentId);
  const existingSystemShipWithSameImo = useMemo(() => {
    const normalizedImo = shipMasterDraft.imo.trim();
    if (!normalizedImo) return null;

    return (
      systemShipSuggestionsByImo?.docs?.find((ship) => ship.imo?.trim() === normalizedImo) ?? null
    );
  }, [shipMasterDraft.imo, systemShipSuggestionsByImo?.docs]);
  const shipMasterDraftSummary = useMemo(
    () => [
      { label: 'IMO', value: shipMasterDraft.imo || '-' },
      { label: 'Flag', value: shipMasterDraft.flag || '-' },
      { label: 'Call Sign', value: shipMasterDraft.callSign || '-' },
      { label: 'MMSI', value: shipMasterDraft.mmsi || '-' },
    ],
    [shipMasterDraft]
  );

  useEffect(() => {
    if (isOffshoreCategory) return;
    const currentLinks = (form.getValues('linkedHelicopters') || []) as string[];
    if (currentLinks.length === 0) return;
    form.setValue('linkedHelicopters', [], {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [form, isOffshoreCategory]);

  useEffect(() => {
    if (isShipCategory) return;
    if (!form.getValues('linkedWindpark')) return;
    form.setValue('linkedWindpark', null, {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [form, isShipCategory]);

  useEffect(() => {
    if (!isOffshoreCategory) {
      previousOffshoreProviderIdRef.current = null;
      return;
    }

    const nextProviderId = selectedHeliProviderId || null;
    const previousProviderId = previousOffshoreProviderIdRef.current;

    if (previousProviderId !== null && previousProviderId !== nextProviderId) {
      const currentLinks = (form.getValues('linkedHelicopters') || []) as string[];
      if (currentLinks.length > 0) {
        form.setValue('linkedHelicopters', [], {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }

    previousOffshoreProviderIdRef.current = nextProviderId;
  }, [form, isOffshoreCategory, selectedHeliProviderId]);

  const categoryMetadataSchema = selectedCategory?.metadataSchema as JsonSchemaType | undefined;

  const resetFormFromObject = useCallback(
    (obj: PortalObject, opts?: { nameOverride?: string }) => {
      if (!obj) return;
      const assignedCustomerId =
        typeof obj.assignedCustomer === 'object' ? obj.assignedCustomer?.id : obj.assignedCustomer;
      const assignedHeliProviderId =
        typeof obj.assignedHelicopterServiceProvider === 'object'
          ? obj.assignedHelicopterServiceProvider?.id
          : obj.assignedHelicopterServiceProvider;

      const categoryId = typeof obj.category === 'object' ? obj.category?.id : obj.category;

      const nextValues: ObjectFormValues = {
        name: opts?.nameOverride ?? obj.name,
        description: obj.description || '',
        category: categoryId || '',
        systemShip:
          (typeof obj.systemShip === 'object' ? obj.systemShip?.id : obj.systemShip) ?? null,
        activePeriods: Array.isArray(obj.activePeriods)
          ? obj.activePeriods.map((period) => ({
              startDate: period.startDate || '',
              endDate: period.endDate || '',
            }))
          : [],
        assignedCustomer: assignedCustomerId || '',
        assignedHelicopterServiceProvider: assignedHeliProviderId ?? null,
        linkedWindpark:
          (typeof obj.linkedWindpark === 'object' ? obj.linkedWindpark?.id : obj.linkedWindpark) ??
          null,
        linkedHelicopters: Array.isArray(obj.linkedHelicopters)
          ? obj.linkedHelicopters
              .map((entry) => (typeof entry === 'string' ? entry : entry?.id || null))
              .filter((entry): entry is string => Boolean(entry))
          : [],
        dynamicMetadataSchema:
          (obj.dynamicMetadataSchema as JsonSchemaType) || createEmptyMetadataSchema(),
        categoryMetadataValues: parseMetadata(
          obj.categoryMetadataValues as Record<string, unknown> | undefined
        ),
        dynamicMetadataValues: parseMetadata(
          obj.dynamicMetadataValues as Record<string, unknown> | undefined
        ),
      };

      previousHeliProviderIdRef.current =
        typeof assignedHeliProviderId === 'string' ? assignedHeliProviderId : null;
      previousOffshoreProviderIdRef.current =
        typeof assignedHeliProviderId === 'string' ? assignedHeliProviderId : null;
      const categoryMetadataValues =
        (obj.categoryMetadataValues as Record<string, unknown> | undefined) ?? {};
      setShipMasterDraft({
        imo: typeof categoryMetadataValues.imo === 'string' ? categoryMetadataValues.imo : '',
        flag: typeof categoryMetadataValues.flag === 'string' ? categoryMetadataValues.flag : '',
        callSign:
          typeof categoryMetadataValues.callSign === 'string'
            ? categoryMetadataValues.callSign
            : '',
        mmsi: typeof categoryMetadataValues.mmsi === 'string' ? categoryMetadataValues.mmsi : '',
      });
      form.reset(nextValues);
      initialValuesRef.current = nextValues;
    },
    [form]
  );

  const resetFormForShipReuseSelection = useCallback(
    (systemShipId: string, systemShipName: string, opts?: { clearAssignmentValues?: boolean }) => {
      const currentValues = form.getValues();
      const clearAssignmentValues = opts?.clearAssignmentValues === true;
      const nextValues: ObjectFormValues = {
        name: systemShipName,
        description: clearAssignmentValues ? '' : currentValues.description,
        category: selectedCategoryId || '',
        systemShip: systemShipId,
        activePeriods: clearAssignmentValues ? [] : currentValues.activePeriods || [],
        assignedCustomer: currentValues.assignedCustomer || '',
        assignedHelicopterServiceProvider: clearAssignmentValues
          ? ''
          : currentValues.assignedHelicopterServiceProvider || '',
        linkedWindpark: clearAssignmentValues ? null : currentValues.linkedWindpark || null,
        linkedHelicopters: clearAssignmentValues ? [] : currentValues.linkedHelicopters || [],
        dynamicMetadataSchema: clearAssignmentValues
          ? createEmptyMetadataSchema()
          : currentValues.dynamicMetadataSchema || createEmptyMetadataSchema(),
        categoryMetadataValues: clearAssignmentValues
          ? {}
          : currentValues.categoryMetadataValues || {},
        dynamicMetadataValues: clearAssignmentValues
          ? {}
          : currentValues.dynamicMetadataValues || {},
      };

      previousHeliProviderIdRef.current = null;
      previousOffshoreProviderIdRef.current = null;
      setShipMasterDraft(EMPTY_SHIP_MASTER_DRAFT);
      setShipMasterDraftErrors({});
      form.reset(nextValues);
      initialValuesRef.current = nextValues;
      autoPrefilledShipNameRef.current = systemShipName;
    },
    [form, selectedCategoryId]
  );

  useEffect(() => {
    if (!isShipCategory) {
      setShipMasterDraft(EMPTY_SHIP_MASTER_DRAFT);
      autoPrefilledShipNameRef.current = null;
      return;
    }

    if (shipAssignmentMode === 'create-new' || !selectedSystemShip) {
      return;
    }

    const currentName = (form.getValues('name') || '').trim();
    const previousAutoPrefill = autoPrefilledShipNameRef.current;

    if (!currentName || currentName === previousAutoPrefill) {
      form.setValue('name', selectedSystemShip.name, {
        shouldDirty: Boolean(currentName),
        shouldValidate: true,
      });
      autoPrefilledShipNameRef.current = selectedSystemShip.name;
    }
  }, [form, isShipCategory, selectedSystemShip, shipAssignmentMode]);

  const autoLoadedShipAssignmentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isShipCreateMode || shipAssignmentMode !== 'reuse-existing' || !selectedSystemShipId) {
      autoLoadedShipAssignmentIdRef.current = null;
      return;
    }

    if (!selectedSystemShip) {
      return;
    }

    if (!existingVisibleShipAssignmentId) {
      if (autoLoadedShipAssignmentIdRef.current !== '__empty__') {
        const clearAssignmentValues =
          autoLoadedShipAssignmentIdRef.current !== null &&
          autoLoadedShipAssignmentIdRef.current !== '__empty__';
        resetFormForShipReuseSelection(selectedSystemShipId, selectedSystemShip.name, {
          clearAssignmentValues,
        });
        autoLoadedShipAssignmentIdRef.current = '__empty__';
      }
      return;
    }

    if (!existingVisibleShipAssignment) {
      return;
    }

    if (autoLoadedShipAssignmentIdRef.current === existingVisibleShipAssignment.id) {
      return;
    }

    resetFormFromObject(existingVisibleShipAssignment, {
      nameOverride: selectedSystemShip.name,
    });
    autoLoadedShipAssignmentIdRef.current = existingVisibleShipAssignment.id;
  }, [
    existingVisibleShipAssignment,
    existingVisibleShipAssignmentId,
    isShipCreateMode,
    resetFormForShipReuseSelection,
    resetFormFromObject,
    selectedSystemShip,
    selectedSystemShipId,
    shipAssignmentMode,
  ]);

  useEffect(() => {
    if (!isShipCategory) {
      setShipSubmitError(null);
      setShipMasterDraftErrors({});
      return;
    }

    setShipSubmitError(null);
  }, [isShipCategory, selectedSystemShipId, watchedActivePeriods]);

  useEffect(() => {
    if (!isShipCategory) return;
    if (isEditMode || isCopyMode) {
      setShipAssignmentMode(selectedSystemShipId ? 'reuse-existing' : 'create-new');
      return;
    }

    if (selectedSystemShipId) {
      setShipAssignmentMode('reuse-existing');
    }
  }, [isCopyMode, isEditMode, isShipCategory, selectedSystemShipId]);

  useEffect(() => {
    if (isEditMode && object) {
      resetFormFromObject(object);
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    }
  }, [isEditMode, isCopyMode, object, objectToCopy, resetFormFromObject, t]);

  useEffect(() => {
    if (isEditMode || isCopyMode) return;
    if (!initialValuesRef.current) {
      initialValuesRef.current = form.getValues();
    }
  }, [form, isCopyMode, isEditMode]);

  // Update category metadata zod schema when category changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      setCategoryMetadataZodSchema(
        createMetadataZodSchema(categoryMetadataSchema, effectiveGroupKey)
      );
    } else {
      setCategoryMetadataZodSchema(z.object({}));
    }
  }, [categoryMetadataSchema, effectiveGroupKey]);

  // Update dynamic metadata zod schema when object schema changes
  useEffect(() => {
    const objectSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema && Object.keys(objectSchema.properties || {}).length > 0) {
      setDynamicMetadataZodSchema(createMetadataZodSchema(objectSchema, effectiveGroupKey));
    } else {
      setDynamicMetadataZodSchema(z.object({}));
    }
  }, [form.watch('dynamicMetadataSchema'), effectiveGroupKey]);

  // Sanitize category metadata values when category schema changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      if (form.getFieldState('categoryMetadataValues').isDirty) {
        return;
      }

      const currentValues = (form.getValues('categoryMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, categoryMetadataSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('categoryMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [categoryMetadataSchema, form]);

  // Sanitize dynamic metadata values when object schema changes
  useEffect(() => {
    const objectSchema = form.getValues('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema) {
      if (form.getFieldState('dynamicMetadataValues').isDirty) {
        return;
      }

      const currentValues = (form.getValues('dynamicMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, objectSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('dynamicMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [form.watch('dynamicMetadataSchema'), form]);

  // Initialize drawer schema when opening
  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    if (drawerSchema) {
      form.setValue('dynamicMetadataSchema', drawerSchema);
      setDynamicMetadataZodSchema(createMetadataZodSchema(drawerSchema, effectiveGroupKey));
    }
    setIsMetadataDrawerOpen(false);
  };

  const discardChanges = useCallback(() => {
    if (isEditMode && currentObject) {
      resetFormFromObject(currentObject);
      setViewMode('read');
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    } else if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
    }
    setDrawerSchema(undefined);
    setIsMetadataDrawerOpen(false);
  }, [currentObject, form, isCopyMode, isEditMode, objectToCopy, resetFormFromObject, t]);

  const currentObjectId = currentObject?.id ?? id ?? '';

  const linkedWindparkIdForRead = useMemo(
    () =>
      typeof currentObject?.linkedWindpark === 'object'
        ? currentObject?.linkedWindpark?.id
        : currentObject?.linkedWindpark,
    [currentObject?.linkedWindpark]
  );

  const { data: linkedWindparkForRead } = useObjects({
    where: linkedWindparkIdForRead
      ? {
          id: {
            equals: linkedWindparkIdForRead,
          },
        }
      : undefined,
    limit: 1,
    disabled: !linkedWindparkIdForRead,
  });

  const linkedWindparkReferenceItem = useMemo(() => {
    if (!linkedWindparkIdForRead) return null;
    const linkedWindpark = linkedWindparkForRead?.docs?.[0];
    return {
      id: linkedWindparkIdForRead,
      label: linkedWindpark?.name || linkedWindparkIdForRead,
      href: getObjectDetailHref({
        objectId: linkedWindparkIdForRead,
        domain: effectiveDomain,
        canReadObjects,
      }),
    };
  }, [canReadObjects, effectiveDomain, linkedWindparkForRead?.docs, linkedWindparkIdForRead]);

  // Helicopter objects: the offshore objects that link to them (reverse lookup).
  const { data: linkedOffshoreObjectsForRead } = useObjects({
    where: {
      and: [
        {
          linkedHelicopters: {
            contains: currentObjectId,
          },
        },
        {
          category: {
            in: offshoreCategoryIds,
          },
        },
      ],
    },
    limit: 1000,
    disabled: !isHelicopterCategory || !currentObjectId || offshoreCategoryIds.length === 0,
  });

  const linkedOffshoreReferenceItems = useMemo(
    () =>
      (linkedOffshoreObjectsForRead?.docs || []).map((doc) => ({
        id: String(doc.id),
        label: doc.name || String(doc.id),
        href: getObjectDetailHref({
          objectId: doc.id,
          domain: effectiveDomain,
          canReadObjects,
        }),
      })),
    [linkedOffshoreObjectsForRead?.docs, effectiveDomain, canReadObjects]
  );

  // Offshore objects store their linked helicopters directly; names are resolved
  // server-side (reference labels), so read them straight from the document.
  const linkedHelicopterReferenceItems = useMemo(() => {
    if (!Array.isArray(currentObject?.linkedHelicopters)) return [];
    const items: { id: string; label: string; href: string | null }[] = [];
    for (const entry of currentObject.linkedHelicopters) {
      const id = typeof entry === 'string' ? entry : entry?.id || null;
      if (!id) continue;
      const label = typeof entry === 'string' ? id : entry?.name || id;
      items.push({
        id,
        label,
        href: getObjectDetailHref({ objectId: id, domain: effectiveDomain, canReadObjects }),
      });
    }
    return items;
  }, [currentObject?.linkedHelicopters, effectiveDomain, canReadObjects]);

  const { data: linkedShipsForWindparkObject } = useObjects({
    where: currentObjectId
      ? {
          and: [
            {
              linkedWindpark: {
                equals: currentObjectId,
              },
            },
            ...(shipCategoryIds.length > 0
              ? [
                  {
                    category: {
                      in: shipCategoryIds,
                    },
                  },
                ]
              : []),
          ],
        }
      : undefined,
    sort: 'name',
    limit: 1000,
    disabled: !isWindparkCategory || !currentObjectId,
  });

  const activeLinkedShipsForWindparkObject = useMemo(() => {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    return (linkedShipsForWindparkObject?.docs || []).filter((doc) =>
      (doc.activePeriods || []).some(
        (period) =>
          period.startDate && period.endDate && period.startDate <= today && period.endDate >= today
      )
    );
  }, [linkedShipsForWindparkObject?.docs]);

  const linkedShipReferenceItems = useMemo(
    () =>
      activeLinkedShipsForWindparkObject.map((doc) => ({
        id: String(doc.id),
        label: doc.name || String(doc.id),
        href: getObjectDetailHref({
          objectId: doc.id,
          domain: effectiveDomain,
          canReadObjects,
        }),
      })),
    [activeLinkedShipsForWindparkObject, canReadObjects, effectiveDomain]
  );
  const linkedWindparkSelectionWhere = useMemo(() => {
    if (windparkCategoryIds.length === 0) return undefined;

    return {
      and: [
        {
          category: {
            in: windparkCategoryIds,
          },
        },
        ...(shipWindparkSelectionOwner
          ? [
              {
                [shipWindparkSelectionOwner.field]: {
                  equals: shipWindparkSelectionOwner.id,
                },
              },
            ]
          : []),
      ],
    };
  }, [shipWindparkSelectionOwner, windparkCategoryIds]);

  const canEditOffshoreLinkedHelicopters =
    isOffshoreCategory &&
    !isViewAsActive &&
    (isEffectiveGmnAdmin || isEffectiveCustomerAdmin || isEffectiveCustomerUser);
  const canEditShipLinkedWindpark =
    isShipCategory &&
    !isViewAsActive &&
    (isEffectiveGmnAdmin || isEffectiveCustomerAdmin || isEffectiveCustomerUser);

  const { dialog: unsavedDialog, allowNextNavigation } = useUnsavedChangesGuard({
    when: form.formState.isDirty && viewMode === 'edit' && !isViewAsActive,
    onDiscard: discardChanges,
  });

  const previousViewAsRef = useRef(false);
  const previousViewAsUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEditMode) return;

    if (isViewAsActive) {
      const currentViewAsUserId = viewAsUser?.id ?? null;
      const userChanged = previousViewAsUserRef.current !== currentViewAsUserId;
      if (!previousViewAsRef.current || userChanged) {
        discardChanges();
      }

      setViewMode(viewAsUpdateAllowed ? 'edit' : 'read');
      previousViewAsUserRef.current = currentViewAsUserId;
    } else if (previousViewAsRef.current) {
      setViewMode('read');
      previousViewAsUserRef.current = null;
    }

    previousViewAsRef.current = isViewAsActive;
  }, [discardChanges, isEditMode, isViewAsActive, viewAsUpdateAllowed, viewAsUser?.id]);

  useEffect(() => {
    if (!isViewAsActive || viewAsUpdateAllowed) return;
    if (viewMode !== 'read' || form.formState.isDirty) {
      discardChanges();
    }
  }, [discardChanges, form.formState.isDirty, isViewAsActive, viewAsUpdateAllowed, viewMode]);

  const handleSubmit = async (data: ObjectFormValues) => {
    if (!canSaveObject || isSaving) return;

    if (isShipCreateMode && shipAssignmentMode === 'reuse-existing' && !data.systemShip) {
      form.setError('systemShip', {
        type: 'manual',
        message: t('objects.ships.systemShipReuseRequired'),
      });
      toast.error(t('objects.ships.systemShipReuseRequired'));
      return;
    }

    if (isShipCreateMode && shipAssignmentMode === 'create-new' && !data.systemShip) {
      if (!shipMasterDraft.imo.trim()) {
        const message = t('objects.ships.imoRequired');
        setShipMasterDraftErrors({ imo: message });
        toast.error(message);
        return;
      }

      if (existingSystemShipWithSameImo) {
        const message = t('objects.ships.imoDuplicate');
        setShipMasterDraftErrors({ imo: message });
        toast.error(message);
        return;
      }
    }

    // Let relation combobox selections flush before snapshotting linked IDs for save.
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 0);
    });

    const payloadData = {
      ...data,
      systemShip: data.systemShip || null,
      activePeriods: (data.activePeriods || []).filter(
        (period) => period.startDate && period.endDate
      ),
      linkedWindpark: data.linkedWindpark || null,
      categoryMetadataValues: sanitizeMetadataValues(
        data.categoryMetadataValues as Record<string, unknown> | undefined,
        categoryMetadataSchema
      ),
      dynamicMetadataValues: sanitizeMetadataValues(
        data.dynamicMetadataValues as Record<string, unknown> | undefined,
        data.dynamicMetadataSchema as JsonSchemaType | undefined
      ),
    } as DeepPartial<PortalObject>;

    try {
      setIsSaving(true);
      setShipSubmitError(null);

      if (isShipCategory && shipAssignmentMode === 'create-new' && !payloadData.systemShip) {
        payloadData.categoryMetadataValues = {
          ...(payloadData.categoryMetadataValues as Record<string, unknown> | undefined),
          imo: shipMasterDraft.imo.trim() || null,
          flag: shipMasterDraft.flag.trim() || null,
          callSign: shipMasterDraft.callSign.trim() || null,
          mmsi: shipMasterDraft.mmsi.trim() || null,
        };
      }

      if (isEditMode) {
        if (form.formState.isDirty) {
          await updateObject.mutateAsync({ id, data: payloadData });
        }

        toast.success(t('objects.objectUpdatedSuccessfully'));
        setViewMode('read');
      } else {
        const reusedObjectId = hasExistingVisibleShipAssignment
          ? existingVisibleShipAssignmentId
          : null;

        if (reusedObjectId) {
          await updateObject.mutateAsync({
            id: reusedObjectId,
            data: payloadData,
          });
          toast.success(t('objects.objectUpdatedSuccessfully'));
          allowNextNavigation();
          const createdCategory =
            typeof data.category === 'string' && data.category ? data.category : null;
          const categoryQuery = createdCategory ? `?category=${createdCategory}` : '';
          const basePath = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';
          navigate(`${basePath}/${reusedObjectId}/details${categoryQuery}`);
          return;
        }

        const createdObject = await createObject.mutateAsync(payloadData);
        toast.success(t('objects.objectCreatedSuccessfully'));
        allowNextNavigation();
        const createdCategory =
          typeof data.category === 'string' && data.category ? data.category : null;
        const categoryQuery = createdCategory ? `?category=${createdCategory}` : '';
        const basePath = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';
        navigate(`${basePath}/${createdObject.doc.id}/details${categoryQuery}`);
      }
    } catch (error) {
      if (error instanceof PayloadApiError) {
        const firstError =
          Array.isArray(error.response?.errors) && error.response.errors.length > 0
            ? (error.response.errors[0] as {
                message?: string;
                data?: {
                  index?: number;
                  leftIndex?: number;
                  rightIndex?: number;
                  conflictingObjectId?: string;
                  existingObjectId?: string;
                };
              })
            : null;

        if (firstError?.message === 'ShipActivePeriodInvalidRange') {
          const index = firstError.data?.index ?? 0;
          form.setError(`activePeriods.${index}.startDate`, {
            type: 'server',
            message: t('validation.StartDateAfterEndDate'),
          });
          form.setError(`activePeriods.${index}.endDate`, {
            type: 'server',
            message: t('validation.EndDateBeforeStartDate'),
          });
          toast.error(t('validation.pleaseCorrectErrors'));
          return;
        }

        if (firstError?.message === 'ShipActivePeriodsOverlap') {
          setShipSubmitError(t('objects.ships.activePeriodsConflictExisting'));
          toast.error(t('objects.ships.activePeriodsConflictExisting'));
          return;
        }

        if (firstError?.message === 'ShipActivePeriodsOverlapOwnPeriods') {
          const leftIndex = firstError.data?.leftIndex ?? 0;
          const rightIndex = firstError.data?.rightIndex ?? 0;
          const message = t('objects.ships.activePeriodsConflictOwn');

          form.setError(`activePeriods.${leftIndex}.startDate`, {
            type: 'server',
            message,
          });
          form.setError(`activePeriods.${rightIndex}.startDate`, {
            type: 'server',
            message,
          });
          setShipSubmitError(message);
          toast.error(message);
          return;
        }

        if (firstError?.message === 'ShipAssignmentAlreadyExistsForCustomer') {
          const message = t('objects.ships.reuseExistingAssignmentRequired');
          setShipSubmitError(message);
          toast.error(message);
          return;
        }

        if (firstError?.message === 'ShipImoAlreadyExists') {
          const message = t('objects.ships.imoDuplicate');
          setShipMasterDraftErrors({ imo: message });
          toast.error(message);
          return;
        }

        if (firstError?.message === 'ShipImoRequired') {
          const message = t('objects.ships.imoRequired');
          setShipMasterDraftErrors({ imo: message });
          toast.error(message);
          return;
        }

        if (firstError?.message === 'LinkedWindparkAssignmentMismatch') {
          const message = t('objects.ships.linkedWindparkAssignmentMismatch');
          form.setError('linkedWindpark', {
            type: 'server',
            message,
          });
          toast.error(message);
          return;
        }
      }

      toast.error(t('objects.errorSavingObject'));
    } finally {
      setIsSaving(false);
    }
  };

  const getPageTitle = () => {
    if (isShipCategory) {
      if (!isEditMode) {
        return isGmnUser
          ? t('objects.ships.assignmentTitleCreate')
          : t('objects.ships.assignmentTitleCustomerCreate');
      }

      return isGmnUser
        ? t('objects.ships.assignmentTitle')
        : t('objects.ships.assignmentTitleCustomer');
    }

    if (!isGmnUser) {
      return isEditMode ? t('objects.objectDetails') : t('objects.newObject');
    }
    return isEditMode
      ? t('objects.editObject')
      : isCopyMode
        ? t('objects.copyObject')
        : t('objects.newObject');
  };

  const getCreateHint = () => {
    if (isShipCategory) {
      return isGmnUser
        ? t('objects.ships.assignmentHint')
        : t('objects.ships.assignmentHintCustomer');
    }

    return t('objects.createNewObjectHint');
  };

  const getObjectLink = () => {
    const objectId = id ?? currentObject?.id;
    if (!objectId) return null;
    const routerBaseRaw = import.meta.env.VITE_APP_ROUTER_BASE || '';
    const routerBase = routerBaseRaw
      ? routerBaseRaw.startsWith('/')
        ? routerBaseRaw
        : `/${routerBaseRaw}`
      : '';
    const normalizedBase = routerBase.endsWith('/') ? routerBase.slice(0, -1) : routerBase;
    const path = `${normalizedBase}/objects/${objectId}/details`;
    if (typeof window === 'undefined') return path;
    return `${window.location.origin}${path}`;
  };

  const handleCopyLink = async () => {
    const link = getObjectLink();
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success(t('objects.copyLinkSuccess'));
        return;
      }
      throw new Error('Clipboard unavailable');
    } catch (_error) {
      toast.error(t('objects.copyLinkError'));
    }
  };

  // Tab navigation logic
  const rawTab = isEditMode ? location.pathname.split('/').pop() || 'details' : 'details';
  const currentTab = rawTab === 'files' ? 'details' : rawTab;
  const basePath = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';

  const showPermissionsTab =
    isEditMode &&
    !isViewAsActive &&
    (isEffectiveGmnAdmin || (isEffectiveCustomerAdmin && isObjectManagedByEffectiveUser));
  const showActivityTab = isEditMode && !isForeignReadonlyObjectForCustomer;
  const tabs = [
    { value: 'details', label: t('objects.masterData'), icon: FileText },
    ...(showPermissionsTab
      ? [
          {
            value: 'permissions',
            label: t('permissions.tab'),
            icon: ShieldCheck,
          },
        ]
      : []),
    ...(showActivityTab
      ? [{ value: 'activity', label: t('common.activity'), icon: Activity }]
      : []),
  ];

  const handleShipAssignmentModeChange = useCallback(
    (value: string) => {
      if (value !== 'reuse-existing' && value !== 'create-new') return;

      setShipAssignmentMode(value);
      setShipSubmitError(null);
      setShipMasterDraftErrors({});
      form.clearErrors('systemShip');

      if (value === 'create-new') {
        form.setValue('systemShip', null, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    },
    [form]
  );

  const renderObjectDetailsCard = () => {
    const showNameField = !isShipCreateMode || shipAssignmentMode === 'create-new';
    const content =
      viewMode === 'edit' ? (
        <div className="grid grid-cols-6 gap-4">
          {isShipCategory ? (
            <div className="col-span-6">
              <Alert className="border-border bg-card">
                <Info className="size-4" />
                <AlertDescription className="space-y-1">
                  <div className="font-medium">{t('objects.ships.assignmentCardTitle')}</div>
                  <div>{t('objects.ships.assignmentCardDescription')}</div>
                </AlertDescription>
              </Alert>
            </div>
          ) : null}

          {isShipCreateMode ? (
            <div className="col-span-6">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    {t('objects.ships.assignmentModeTitle')}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('objects.ships.assignmentModeDescription')}
                  </div>
                </div>

                <RadioGroup
                  value={shipAssignmentMode}
                  onValueChange={handleShipAssignmentModeChange}
                  className="grid gap-3 md:grid-cols-2"
                >
                  <label
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background p-4"
                    htmlFor="ship-mode-reuse-existing"
                  >
                    <RadioGroupItem id="ship-mode-reuse-existing" value="reuse-existing" />
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {t('objects.ships.assignmentModeReuseTitle')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {t('objects.ships.assignmentModeReuseDescription')}
                      </div>
                    </div>
                  </label>
                  <label
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background p-4"
                    htmlFor="ship-mode-create-new"
                  >
                    <RadioGroupItem id="ship-mode-create-new" value="create-new" />
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {t('objects.ships.assignmentModeCreateTitle')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {t('objects.ships.assignmentModeCreateDescription')}
                      </div>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>
          ) : null}

          {showNameField ? (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem
                  className={
                    isShipCategory
                      ? 'col-span-6 md:col-span-3'
                      : 'col-span-6 md:col-span-3 lg:col-span-2'
                  }
                >
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel required>{t('common.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={formDisabled || isShipNameMirrored} />
                    </FormControl>
                    {isShipNameMirrored ? (
                      <div className="text-xs text-muted-foreground">
                        {t('objects.ships.systemShipInherited')}
                      </div>
                    ) : null}
                    {isShipCategory &&
                    viewMode === 'edit' &&
                    !selectedSystemShipId &&
                    (systemShipSuggestions?.docs?.length ?? 0) > 0 ? (
                      <div className="space-y-2 pt-2">
                        <div className="text-xs text-muted-foreground">
                          {t('objects.ships.systemShipSuggestions')}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {systemShipSuggestions?.docs?.map((ship) => (
                            <Button
                              key={ship.id}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShipAssignmentMode('reuse-existing');
                                form.clearErrors('systemShip');
                                form.setValue('systemShip', ship.id, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                if (!(form.getValues('name') || '').trim()) {
                                  form.setValue('name', ship.name, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                  autoPrefilledShipNameRef.current = ship.name;
                                }
                              }}
                            >
                              {ship.name}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : null}

          {/* Category selector - only for GMN users and only on create; the category is fixed
              once the object exists (it is still shown in the page header) */}
          {isGmnUser && !isEditMode && (
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objectCategories.category')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value || ''}
                        onSelect={field.onChange}
                        collectionKey="object-categories"
                        searchKey="name"
                        clearValue=""
                        // JUH dispatchers keep the preselected island/clinic/land/fuel-station
                        // category; they may not switch to other types.
                        disabled={formDisabled || isEffectiveJuhDispatcher}
                        texts={{
                          selectItemMsg: t('objectCategories.selectCategory'),
                          searchPlaceholder: t('objectCategories.selectCategory'),
                          noResultsMsg: t('objectCategories.noCategory'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {isShipCategory && !isEditMode && shipAssignmentMode === 'reuse-existing' && (
            <FormField
              control={form.control}
              name="systemShip"
              render={({ field }) => (
                <FormItem className="col-span-6">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objects.ships.systemShipLabel')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? ''}
                        onSelect={field.onChange}
                        collectionKey="system-ships"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        texts={{
                          selectItemMsg: t('objects.ships.systemShipLabel'),
                          searchPlaceholder: t('objects.ships.searchPlaceholder'),
                          noResultsMsg: t('common.noResults'),
                        }}
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground">
                      {!isEditMode && isShipCreateMode
                        ? t('objects.ships.systemShipReuseHint')
                        : t('objects.ships.systemShipDescription')}
                    </div>
                    {selectedSystemShipId ? (
                      <div className="space-y-3 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-3">
                        <div className="text-xs font-medium text-muted-foreground">
                          {t('objects.ships.systemShipDetails')}
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {selectedSystemShipSummary.map((item) => (
                            <div key={item.label} className="rounded-md bg-background px-3 py-2">
                              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                {item.label}
                              </div>
                              <div className="text-sm font-medium">{item.value}</div>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('objects.ships.systemShipInherited')}
                        </div>
                        {hasExistingVisibleShipAssignment ? (
                          <Alert className="border-border bg-background">
                            <Info className="size-4" />
                            <AlertDescription className="text-xs text-muted-foreground">
                              {t('objects.ships.existingAssignmentLoaded')}
                            </AlertDescription>
                          </Alert>
                        ) : null}
                      </div>
                    ) : isShipCreateMode ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/40 px-3 py-3 text-xs text-muted-foreground">
                        {t('objects.ships.systemShipReuseEmpty')}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border bg-muted/40 px-3 py-3 text-xs text-muted-foreground">
                        {t('objects.ships.systemShipCreateNew')}
                      </div>
                    )}
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {isShipCreateMode && shipAssignmentMode === 'create-new' ? (
            <div className="col-span-6">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium">{t('objects.ships.masterDataTitle')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('objects.ships.masterDataDescription')}
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <FormLabel required>{t('objects.ships.imoLabel')}</FormLabel>
                    <Input
                      aria-label={t('objects.ships.imoLabel')}
                      aria-required
                      aria-invalid={Boolean(shipMasterDraftErrors.imo)}
                      value={shipMasterDraft.imo}
                      onChange={(event) => updateShipMasterDraft('imo', event.target.value)}
                      disabled={formDisabled}
                    />
                    {shipMasterDraftErrors.imo ? (
                      <p className="text-sm font-medium text-destructive">
                        {shipMasterDraftErrors.imo}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <FormLabel>{t('objects.ships.flagLabel')}</FormLabel>
                    <Input
                      aria-label={t('objects.ships.flagLabel')}
                      value={shipMasterDraft.flag}
                      onChange={(event) => updateShipMasterDraft('flag', event.target.value)}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <FormLabel>{t('objects.ships.callSignLabel')}</FormLabel>
                    <Input
                      aria-label={t('objects.ships.callSignLabel')}
                      value={shipMasterDraft.callSign}
                      onChange={(event) => updateShipMasterDraft('callSign', event.target.value)}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <FormLabel>{t('objects.ships.mmsiLabel')}</FormLabel>
                    <Input
                      aria-label={t('objects.ships.mmsiLabel')}
                      value={shipMasterDraft.mmsi}
                      onChange={(event) => updateShipMasterDraft('mmsi', event.target.value)}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {shipMasterDraftSummary.map((item) => (
                    <div key={item.label} className="rounded-md bg-muted/40 px-3 py-2">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {item.label}
                      </div>
                      <div className="text-sm font-medium">{item.value}</div>
                    </div>
                  ))}
                </div>
                {existingSystemShipWithSameImo ? (
                  <Alert className="border-border bg-background">
                    <Info className="size-4" />
                    <AlertDescription className="space-y-2 text-xs text-muted-foreground">
                      <div>{t('objects.ships.imoDuplicateHint')}</div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShipAssignmentMode('reuse-existing');
                          form.clearErrors('systemShip');
                          form.setValue('systemShip', existingSystemShipWithSameImo.id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          form.setValue('name', existingSystemShipWithSameImo.name, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setShipSubmitError(null);
                          setShipMasterDraftErrors({});
                        }}
                      >
                        {t('objects.ships.useExistingShip')}
                      </Button>
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>
            </div>
          ) : null}

          {!isGmnUser && isShipCategory ? (
            <div className="col-span-6 md:col-span-3">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.ships.assignmentOrganization')}
                </div>
                <div className="text-sm font-medium">
                  {t('objects.ships.assignmentOrganizationAutomatic', {
                    name:
                      (typeof effectiveUser?.customer === 'object' &&
                        effectiveUser.customer?.name) ||
                      '-',
                  })}
                </div>
              </div>
            </div>
          ) : null}

          {/* Only show assigned customer field for GMN admins not in customer view mode */}
          {isGmnAdminLevel && (
            <FormField
              control={form.control}
              name="assignedCustomer"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedCustomer')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{
                          or: [
                            { customerType: { equals: 'customer' } },
                            { customerType: { exists: false } },
                          ],
                        }}
                        texts={{
                          selectItemMsg: t('customers.selectCustomer'),
                          searchPlaceholder: t('customers.searchCustomers'),
                          noResultsMsg: t('customers.noCustomersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {isGmnAdminLevel ? (
            <FormField
              control={form.control}
              name="assignedHelicopterServiceProvider"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedHelicopterServiceProvider')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{
                          customerType: { equals: 'heli-provider' },
                        }}
                        texts={{
                          selectItemMsg: t('customers.selectHelicopterServiceProvider'),
                          searchPlaceholder: t('customers.searchHelicopterServiceProviders'),
                          noResultsMsg: t('customers.noHelicopterServiceProvidersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : currentObject ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('customers.assignedHelicopterServiceProvider')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedHelicopterServiceProvider === 'object'
                        ? currentObject?.assignedHelicopterServiceProvider?.name
                        : assignedHeliProviderForRead?.name
                    }
                    href={assignedHelicopterProviderHref}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {isHelicopterCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedOffshoreObjects')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedOffshoreReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          {canEditShipLinkedWindpark ? (
            <FormField
              control={form.control}
              name="linkedWindpark"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objects.linkedWindpark')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value ?? undefined}
                        onSelect={field.onChange}
                        collectionKey="objects"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled || windparkCategoryIds.length === 0}
                        customWhere={linkedWindparkSelectionWhere}
                        texts={{
                          selectItemMsg: t('objects.selectLinkedWindpark'),
                          searchPlaceholder: t('objects.searchLinkedWindpark'),
                          noResultsMsg: t('objects.noLinkedWindparkFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : isShipCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedWindpark')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={linkedWindparkReferenceItem?.label}
                    href={linkedWindparkReferenceItem?.href}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {canEditOffshoreLinkedHelicopters && isOffshoreCategory ? (
            <FormField
              control={form.control}
              name="linkedHelicopters"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objects.linkedHelicopters')}</FormLabel>
                    <FormControl>
                      <MultiSearchCombobox
                        value={(field.value || []) as string[]}
                        onSelect={field.onChange}
                        collectionKey="objects"
                        searchKey="name"
                        disabled={
                          formDisabled ||
                          !selectedHeliProviderId ||
                          helicopterCategoryIds.length === 0
                        }
                        customWhere={
                          selectedHeliProviderId && helicopterCategoryIds.length > 0
                            ? {
                                and: [
                                  {
                                    assignedHelicopterServiceProvider: {
                                      equals: selectedHeliProviderId,
                                    },
                                  },
                                  {
                                    category: {
                                      in: helicopterCategoryIds,
                                    },
                                  },
                                ],
                              }
                            : undefined
                        }
                        texts={{
                          selectItemMsg: t('objects.selectLinkedHelicopters'),
                          searchPlaceholder: t('objects.searchLinkedHelicopters'),
                          noResultsMsg: t('objects.noLinkedHelicoptersFound'),
                        }}
                      />
                    </FormControl>
                    {!selectedHeliProviderId ? (
                      <p className="text-xs text-muted-foreground">
                        {t('objects.linkedHelicoptersRequiresProvider')}
                      </p>
                    ) : null}
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          ) : isOffshoreCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedHelicopters')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedHelicopterReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          {isWindparkCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.linkedShips')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedShipReferenceItems} />
                </div>
              </div>
            </div>
          ) : null}

          {isShipCategory ? (
            <div className="col-span-6">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">
                      {t('objects.ships.activePeriodsTitle')}
                    </div>
                  </div>
                  {viewMode === 'edit' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => appendActivePeriod({ startDate: '', endDate: '' })}
                    >
                      <CalendarRange className="mr-2 size-4" />
                      {t('objects.ships.addPeriod')}
                    </Button>
                  ) : null}
                </div>

                {selectedSystemShipId && occupiedShipPeriods.length > 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/40 px-3 py-3">
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('objects.ships.occupiedPeriods')}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {occupiedShipPeriods.map((period, index) => (
                        <Badge
                          key={`${period.startDate}-${period.endDate}-${index}`}
                          variant="secondary"
                        >
                          {formatDay(period.startDate)} - {formatDay(period.endDate)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}

                {shipSubmitError ? (
                  <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertDescription>{shipSubmitError}</AlertDescription>
                  </Alert>
                ) : null}

                {viewMode === 'edit' ? (
                  <div className="space-y-3">
                    {activePeriodFields.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        {t('objects.ships.noPeriodsDefined')}
                      </div>
                    ) : null}
                    {activePeriodFields.map((period, index) => (
                      <div key={period.id} className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                        <FormField
                          control={form.control}
                          name={`activePeriods.${index}.startDate`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('objects.ships.periodStartLabel')}</FormLabel>
                              <FormControl>
                                <DatePicker
                                  date={toPickerDate(field.value)}
                                  setDate={(value) => field.onChange(toIsoDay(value))}
                                  maxDate={toPickerDate(
                                    form.getValues(`activePeriods.${index}.endDate`)
                                  )}
                                  disabledRanges={occupiedShipPeriods}
                                  disabled={formDisabled}
                                  showClearButton={false}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`activePeriods.${index}.endDate`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('objects.ships.periodEndLabel')}</FormLabel>
                              <FormControl>
                                <DatePicker
                                  date={toPickerDate(field.value)}
                                  setDate={(value) => field.onChange(toIsoDay(value))}
                                  minDate={toPickerDate(
                                    form.getValues(`activePeriods.${index}.startDate`)
                                  )}
                                  disabledRanges={occupiedShipPeriods}
                                  disabled={formDisabled}
                                  showClearButton={false}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="flex items-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeActivePeriod(index)}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : currentObject?.activePeriods?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {currentObject.activePeriods.map((period, index) => (
                      <Badge
                        key={`${period.startDate}-${period.endDate}-${index}`}
                        variant="outline"
                      >
                        {[period.startDate, period.endDate]
                          .filter(Boolean)
                          .map((value) => DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy'))
                          .join(' - ')}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    {t('objects.ships.noPeriodsDefined')}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="col-span-6">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('common.description')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} disabled={formDisabled} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-4">
          {!isShipCategory ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('common.name')}
                </div>
                <div className="text-sm font-medium">{currentObject?.name || '-'}</div>
              </div>
            </div>
          ) : null}
          {isShipCategory
            ? selectedSystemShipSummary.map((item) => (
                <div key={item.label} className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      {item.label}
                    </div>
                    <div className="text-sm font-medium">{item.value}</div>
                  </div>
                </div>
              ))
            : null}
          {currentObject?.assignedCustomer ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {isShipCategory ? t('customers.customer') : t('customers.assignedCustomer')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedCustomer === 'object'
                        ? currentObject?.assignedCustomer?.name
                        : assignedCustomerForRead?.name
                    }
                    href={assignedCustomerHref}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {currentObject && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('customers.assignedHelicopterServiceProvider')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={
                      typeof currentObject?.assignedHelicopterServiceProvider === 'object'
                        ? currentObject?.assignedHelicopterServiceProvider?.name
                        : assignedHeliProviderForRead?.name
                    }
                    href={assignedHelicopterProviderHref}
                  />
                </div>
              </div>
            </div>
          )}

          {currentObject && isHelicopterCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedOffshoreObjects')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedOffshoreReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {isOffshoreCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedHelicopters')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedHelicopterReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {isWindparkCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedShips')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceList items={linkedShipReferenceItems} />
                </div>
              </div>
            </div>
          )}

          {currentObject && isShipCategory && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('objects.linkedWindpark')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={linkedWindparkReferenceItem?.label}
                    href={linkedWindparkReferenceItem?.href}
                  />
                </div>
              </div>
            </div>
          )}
          {currentObject && isShipCategory ? (
            <div className="col-span-6">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('objects.ships.activePeriodsTitle')}
                </div>
                {currentObject.activePeriods?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {currentObject.activePeriods.map((period, index) => (
                      <Badge
                        key={`${period.startDate}-${period.endDate}-${index}`}
                        variant="outline"
                      >
                        {[period.startDate, period.endDate]
                          .filter(Boolean)
                          .map((value) => DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy'))
                          .join(' - ')}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    {t('objects.ships.noPeriodsDefined')}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <div className="col-span-6">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('common.description')}
              </div>
              <div className="text-sm text-foreground whitespace-pre-wrap wrap-break-words">
                {currentObject?.description || '-'}
              </div>
            </div>
          </div>
        </div>
      );

    // Keep master data consistent with metadata sections: section header + field cards only.
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <h4 className="text-sm font-semibold text-foreground">{t('objects.masterData')}</h4>
          <div className="h-px flex-1 bg-border" />
        </div>
        {content}
      </div>
    );
  };

  const hasCategoryMetadata =
    categoryMetadataSchema && Object.keys(categoryMetadataSchema.properties || {}).length > 0;

  const dynamicMetadataSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
  const hasCustomMetadata =
    dynamicMetadataSchema && Object.keys(dynamicMetadataSchema.properties || {}).length > 0;

  const getOrderedFieldNames = (metadataSchema: JsonSchemaType) => {
    const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
    return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
  };

  const getKnownGroupIds = (metadataSchema: JsonSchemaType) =>
    new Set((metadataSchema.groups ?? []).map((g) => g.id));

  const getFieldsForGroup = (metadataSchema: JsonSchemaType, groupId: string) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    return ordered.filter((name) => {
      const fieldDef = metadataSchema.properties?.[name];
      if (fieldDef?.groupId !== groupId) return false;
      return getFieldPermission(fieldDef, effectiveGroupKey).read;
    });
  };

  const getUngroupedFields = (metadataSchema: JsonSchemaType) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    const knownGroupIds = getKnownGroupIds(metadataSchema);
    return ordered.filter((name) => {
      const field = metadataSchema.properties?.[name];
      if (!field) return false;
      const fieldGroupId = field.groupId;
      if (!fieldGroupId) {
        return getFieldPermission(field, effectiveGroupKey).read;
      }
      if (knownGroupIds.has(fieldGroupId)) return false;
      return getFieldPermission(field, effectiveGroupKey).read;
    });
  };

  const categoryGroupSections = useMemo(() => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return [];
    const visibleFieldNames = (fieldNames: string[]) =>
      isShipCategory
        ? fieldNames.filter(
            (fieldName) =>
              fieldName !== 'imo' &&
              fieldName !== 'flag' &&
              fieldName !== 'callSign' &&
              fieldName !== 'mmsi'
          )
        : fieldNames;
    const groups = [...(categoryMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-category-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: visibleFieldNames(getFieldsForGroup(categoryMetadataSchema, group.id)),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = visibleFieldNames(getUngroupedFields(categoryMetadataSchema));
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-category-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    categoryMetadataSchema,
    hasCategoryMetadata,
    locale,
    selectedCategoryId,
    t,
    effectiveGroupKey,
    isShipCategory,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(categoryMetadataSchema?.groups ?? []),
  ]);

  const customGroupSections = useMemo(() => {
    if (!hasCustomMetadata || !dynamicMetadataSchema) return [];
    const groups = [...(dynamicMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-custom-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(dynamicMetadataSchema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(dynamicMetadataSchema);
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-custom-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    dynamicMetadataSchema,
    hasCustomMetadata,
    locale,
    t,
    effectiveGroupKey,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(dynamicMetadataSchema?.groups ?? []),
  ]);

  const categoryMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'categoryMetadataValues',
  }) as Record<string, unknown> | undefined;

  const dynamicMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'dynamicMetadataValues',
  }) as Record<string, unknown> | undefined;

  const getGroupStatusIndicator = useCallback(
    (
      metadataSchema: JsonSchemaType | undefined,
      fieldNames: string[],
      values: Record<string, unknown> | undefined
    ): {
      statusValue: StatusValue | undefined;
      statusOptions?: StatusOption[];
      icon?: string;
    } | null => {
      if (!metadataSchema?.properties) return null;

      for (const fieldName of fieldNames) {
        const fieldDef = metadataSchema.properties[fieldName] as
          | (Record<string, unknown> & {
              type?: string;
              isGroupIndicator?: boolean;
            })
          | undefined;
        if (!fieldDef) continue;

        if (fieldDef.type === 'status' && fieldDef.isGroupIndicator === true) {
          const statusOptions =
            ensureStatusOptionsWithIds(
              (fieldDef.statusOptions as StatusOption[] | undefined) ?? []
            ) ?? [];
          const icon = fieldDef.icon as string | undefined;
          const statusValue = values?.[fieldName] as StatusValue | undefined;
          return { statusValue, statusOptions, icon };
        }
      }

      return null;
    },
    []
  );

  const objectFiles = useMemo(
    () => (Array.isArray(currentObject?.files) ? currentObject?.files : []),
    [currentObject?.files]
  );
  const canManageFiles = isEffectiveGmnAdmin;
  const canEditFiles = canManageFiles && viewMode === 'edit' && !isViewAsActive;
  const canShowFileControls = canManageFiles && (viewMode === 'edit' || isViewAsActive);
  const showFilesSection =
    !!id && (viewMode === 'edit' || objectFiles.length > 0 || canShowFileControls);

  const sidemenuItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: 'master-data' | 'files' | 'category-group' | 'custom-group';
      statusIndicator?: {
        statusValue: StatusValue | undefined;
        statusOptions?: StatusOption[];
        icon?: string;
      } | null;
    }> = [
      {
        id: 'object-master-data',
        label: t('objects.masterData'),
        kind: 'master-data',
      },
    ];

    if (showFilesSection) {
      items.push({
        id: 'object-files',
        label: t('objects.files.title'),
        kind: 'files',
      });
    }

    for (const section of categoryGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        categoryMetadataSchema,
        section.fieldNames,
        categoryMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'category-group',
        statusIndicator,
      });
    }
    for (const section of customGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        dynamicMetadataSchema,
        section.fieldNames,
        dynamicMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'custom-group',
        statusIndicator,
      });
    }

    return items;
  }, [
    categoryGroupSections,
    categoryMetadataSchema,
    categoryMetadataValuesWatch,
    customGroupSections,
    dynamicMetadataSchema,
    dynamicMetadataValuesWatch,
    getGroupStatusIndicator,
    showFilesSection,
    t,
  ]);

  const activeSectionId = useScrollSpy(
    sidemenuItems.map((i) => i.id),
    {
      root: scrollRoot,
      rootMargin: '-15% 0px -75% 0px',
    }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: scrollRoot });
  };

  const renderMetadataSections = ({
    schema,
    metadataSchema,
    valuesPath,
    sections,
  }: {
    schema: z.ZodObject<any>;
    metadataSchema: JsonSchemaType;
    valuesPath: string[];
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataAutoForm
              schema={pickZodObjectFields(schema, section.fieldNames)}
              form={form}
              path={valuesPath}
              metadataSchema={metadataSchema}
              groupKey={effectiveGroupKey}
              disabled={formDisabled}
              disableGrouping={true}
              currentObject={currentObject as unknown as PortalViewObject}
              canReadObjects={canReadObjects}
              domain={effectiveDomain}
            />
          </div>
        ))}
      </div>
    );
  };

  const categoryMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.categoryMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.categoryMetadataValues]
  );
  const customMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.dynamicMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.dynamicMetadataValues]
  );

  const renderMetadataReadSections = ({
    metadataSchema,
    values,
    sections,
  }: {
    metadataSchema: JsonSchemaType;
    values: Record<string, unknown> | undefined;
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataReadView
              metadataSchema={metadataSchema}
              values={values}
              fieldNames={section.fieldNames}
              groupKey={effectiveGroupKey}
              currentObject={currentObject as unknown as PortalViewObject}
              canReadObjects={canReadObjects}
              domain={effectiveDomain}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryMetadataCard = () => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return null;

    return viewMode === 'edit' ? (
      <div>
        {renderMetadataSections({
          schema: categoryMetadataZodSchema,
          metadataSchema: categoryMetadataSchema,
          valuesPath: ['categoryMetadataValues'],
          sections: categoryGroupSections,
        })}
      </div>
    ) : (
      <div>
        {renderMetadataReadSections({
          metadataSchema: categoryMetadataSchema,
          values: categoryMetadataValues,
          sections: categoryGroupSections,
        })}
      </div>
    );
  };

  const renderCustomMetadataCard = () => {
    const showEmptyState = viewMode === 'edit' && isEffectiveGmnAdmin && !isViewAsActive;
    if (!showEmptyState && (!hasCustomMetadata || !dynamicMetadataSchema)) return null;

    return (
      <div>
        <div id="object-custom-metadata" className="scroll-mt-28">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Database className="size-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">
                {t('metadata.customMetadata')}
              </h4>
            </div>
            {isEffectiveGmnAdmin && viewMode === 'edit' && !isViewAsActive && (
              <Button variant="outline" size="sm" onClick={openMetadataDrawer} type="button">
                <Edit3 className="size-3!" />
                {t('metadata.addCustomMetadata')}
              </Button>
            )}
          </div>
        </div>

        {viewMode === 'edit' ? (
          hasCustomMetadata && dynamicMetadataSchema ? (
            renderMetadataSections({
              schema: dynamicMetadataZodSchema,
              metadataSchema: dynamicMetadataSchema,
              valuesPath: ['dynamicMetadataValues'],
              sections: customGroupSections,
            })
          ) : (
            <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
          )
        ) : hasCustomMetadata && dynamicMetadataSchema ? (
          renderMetadataReadSections({
            metadataSchema: dynamicMetadataSchema,
            values: customMetadataValues,
            sections: customGroupSections,
          })
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'details':
        return (
          <div className="grid gap-6">
            <Form {...form}>
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-3">
                  {/* Mobile: jump select */}
                  <div className="lg:hidden">
                    <Select
                      value={activeSectionId ?? sidemenuItems[0]?.id ?? 'object-master-data'}
                      onValueChange={(id) => scrollToSection(id)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sidemenuItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Desktop: sticky sidemenu */}
                  <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
                    <ObjectMetadataSidemenu
                      items={sidemenuItems}
                      activeId={activeSectionId}
                      onSelect={scrollToSection}
                      showCustomSeparator={customGroupSections.length > 0}
                    />
                  </div>
                </div>

                <div className="lg:col-span-9 grid gap-6">
                  <div id="object-master-data" className="scroll-mt-28">
                    {renderObjectDetailsCard()}
                  </div>

                  {showFilesSection && (
                    <div id="object-files" className="scroll-mt-28">
                      <ObjectFiles
                        objectId={id}
                        object={currentObject}
                        canManage={canShowFileControls}
                        readOnly={!canEditFiles}
                        variant={viewMode === 'edit' ? 'full' : 'compact'}
                      />
                    </div>
                  )}

                  {renderCategoryMetadataCard()}

                  {/* Minimal separator between category + custom metadata in the main column */}
                  {hasCategoryMetadata && hasCustomMetadata && (
                    <div className="h-px bg-border my-1" aria-hidden="true" />
                  )}

                  {renderCustomMetadataCard()}
                </div>
              </div>
            </Form>
          </div>
        );
      case 'activity':
        if (!showActivityTab) {
          return null;
        }
        return <ObjectActivity objectId={id} groupKey={effectiveGroupKey} />;
      case 'files':
        return <ObjectFiles objectId={id} object={currentObject} canManage={false} readOnly />;
      case 'permissions':
        if (!showPermissionsTab || !id) {
          return (
            <div className="text-sm text-muted-foreground">{t('permissions.notAuthorized')}</div>
          );
        }
        return (
          <EntityPermissionsTab
            entityId={id}
            entityType="objects"
            customerContexts={prioritizedPermissionCustomerContexts}
          />
        );
      default:
        return null;
    }
  };

  const showHeaderActions = currentTab === 'details';
  const headerTitle = isEditMode && currentObject ? currentObject.name : getPageTitle();

  const assignedCustomerName =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.name
      : assignedCustomerForRead?.name;
  const assignedHeliProviderName =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.name
      : assignedHeliProviderForRead?.name;
  const assignedCustomerHref = getCustomerDetailHref({
    customerId: assignedCustomerId,
    customerType: 'customer',
    domain: effectiveDomain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const assignedHelicopterProviderHref = getCustomerDetailHref({
    customerId: assignedHelicopterServiceProviderId,
    customerType: 'heli-provider',
    domain: effectiveDomain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const categoryHref = getObjectCategoryDetailHref({
    categoryId: selectedCategory?.id,
    isGmnAdmin: isEffectiveGmnAdmin,
  });

  const viewAsUserLabel = useMemo(() => {
    if (!viewAsUser) return '';
    const fullName = `${viewAsUser.firstName ?? ''} ${viewAsUser.lastName ?? ''}`.trim();
    return fullName !== '' ? fullName : viewAsUser.email || viewAsUser.id;
  }, [viewAsUser]);

  const viewAsControls = canUseViewAs ? (
    <div className="flex flex-wrap items-center gap-2">
      {isViewAsActive && viewAsUserLabel ? (
        <Badge variant="outline-solid" data-testid="view-as-indicator">
          <Eye size={14} className="mr-1" />
          {t('permissions.viewAs.activeLabel', { name: viewAsUserLabel })}
        </Badge>
      ) : null}
      {!isViewAsActive ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsViewAsDialogOpen(true)}
          data-testid="view-as-open"
        >
          <Eye size={16} className="mr-2" />
          {t('permissions.viewAs.label')}
        </Button>
      ) : null}
      {isViewAsActive ? (
        <Button type="button" variant="outline" onClick={clearViewAs} data-testid="view-as-exit">
          <X size={16} className="mr-2" />
          {t('permissions.viewAs.exit')}
        </Button>
      ) : null}
    </div>
  ) : null;

  const metaItems =
    isEditMode && currentObject
      ? [
          ...(isGmnUser && currentObject.assignedCustomer
            ? [
                {
                  icon: Users,
                  label:
                    assignedCustomerName && assignedCustomerName.length > 0
                      ? assignedCustomerName
                      : '-',
                  href: assignedCustomerHref ?? undefined,
                },
              ]
            : []),
          ...(currentObject.assignedHelicopterServiceProvider
            ? [
                {
                  icon: Users,
                  label:
                    assignedHeliProviderName && assignedHeliProviderName.length > 0
                      ? t('customers.helicopterServiceProviderLabel', {
                          name: assignedHeliProviderName,
                        })
                      : t('customers.helicopterServiceProviderLabel', {
                          name: '-',
                        }),
                  href: assignedHelicopterProviderHref ?? undefined,
                },
              ]
            : []),
          ...(selectedCategory?.name
            ? [
                {
                  icon: getObjectCategoryIcon(selectedCategory.icon),
                  label: selectedCategory.name,
                  href: categoryHref ?? undefined,
                },
              ]
            : []),
        ]
      : undefined;

  const headerIcon = selectedCategory ? getObjectCategoryIcon(selectedCategory.icon) : Box;

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref,
        backLabel: t('objects.backToOverview'),
        title: headerTitle,
        icon: headerIcon,
        isCreateMode: !isEditMode,
        metaItems,
        subtitle: !isEditMode ? getCreateHint() : undefined,
        actions:
          showHeaderActions && isEditMode && (viewMode === 'read' || isViewAsActive) ? (
            <div className="flex flex-wrap items-center gap-3">
              {viewAsControls}
              {viewMode === 'read' && !isViewAsActive ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleCopyLink()}
                  data-testid="object-copy-link"
                >
                  <Link2 size={16} className="mr-2" />
                  {t('objects.copyLink')}
                </Button>
              ) : null}
              {viewMode === 'read' && canSaveObject ? (
                <Button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  data-testid="object-enter-edit"
                >
                  <Edit3 size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : undefined,
        secondaryAction:
          showHeaderActions && !isViewAsActive
            ? isEditMode
              ? viewMode === 'edit'
                ? {
                    label: t('common.discard'),
                    onClick: discardChanges,
                  }
                : undefined
              : {
                  label: t('common.discard'),
                  onClick: () => navigate(backHref),
                }
            : undefined,
        primaryAction:
          showHeaderActions && canSaveObject && (isEditMode ? viewMode === 'edit' : true)
            ? {
                label: isSaving
                  ? isEditMode
                    ? t('common.updating')
                    : t('common.creating')
                  : isEditMode
                    ? t('common.update')
                    : isShipCategory
                      ? isGmnUser
                        ? t('objects.ships.createAssignment')
                        : t('objects.ships.createAssignmentCustomer')
                      : t('common.create'),
                onClick: () => void form.handleSubmit(handleSubmit)(),
                disabled: isSaving || !canSaveObject,
                loading: isSaving,
              }
            : undefined,
      }}
    >
      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />}

      <form id="object-form" onSubmit={form.handleSubmit(handleSubmit)}>
        {renderContent()}
      </form>

      {isEffectiveGmnAdmin && !isViewAsActive && (
        <Dialog open={isMetadataDrawerOpen} onOpenChange={setIsMetadataDrawerOpen}>
          <DialogContent
            className="max-w-[90vw]! w-[1400px]! max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
            showCloseButton={false}
          >
            <div className="flex-1 overflow-hidden">
              <div className="h-[70vh]">
                <MetadataBuilderWithPreview
                  value={drawerSchema}
                  onChange={setDrawerSchema}
                  userGroups={userGroups}
                  header={false}
                  storageKey="gmn.metadataBuilder.objectDrawer.formPreview.open"
                />
              </div>
            </div>
            <DialogFooter className="border-t p-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMetadataDrawerOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" onClick={handleMetadataBuilderSave}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canUseViewAs && isEditMode && id ? (
        <ViewAsDialog
          open={isViewAsDialogOpen}
          onOpenChange={setIsViewAsDialogOpen}
          entityType="objects"
          entityId={id}
          customerContexts={prioritizedPermissionCustomerContexts}
          selectedUserId={viewAsUser?.id ?? null}
          onSelectUser={(user) => {
            setViewAsUserId(user.id);
            setIsViewAsDialogOpen(false);
          }}
        />
      ) : null}

      {unsavedDialog}
    </DetailPageLayout>
  );
}
